import { z } from 'zod';

import { codeSessionModelAcceptsImages } from './models';

/**
 * Files sent with a /code prompt. The browser uploads each file straight to
 * the attachments Blob store through a link `POST /api/code-sessions/attachments`
 * issues, then sends only `{ id, name }` with the prompt. The server reads each
 * file's type and size from the store and saves them on the turn
 * (`CodeSessionTurn.attachments`); the bytes stay in the store.
 *
 * When a turn reaches OpenCode, every file is written into the sandbox.
 * Images and PDFs also go to the model; any other file reaches the agent only
 * as its path, so a large log never lands in the model's context.
 */

/**
 * Per prompt. OpenCode's log event for a prompt carries every file it hands
 * the model as base64, and the turn observer refuses events over 16 MB.
 */
export const CODE_ATTACHMENT_MAX_FILES = 10;
export const CODE_ATTACHMENT_MAX_TOTAL_BYTES = 10 * 1024 * 1024;

/** Why files cannot all go with one prompt; Slack's refusals use the same names. */
export type CodeAttachmentLimitProblem = 'too_many_files' | 'files_too_large';

/** The route that serves a saved file by redirecting to it. */
export function codeAttachmentUrl(sessionId: string, attachmentId: string): string {
  return `/api/code-sessions/${sessionId}/attachments/${attachmentId}`;
}

export const CODE_ATTACHMENT_LIMIT_MESSAGES: Record<CodeAttachmentLimitProblem, string> = {
  too_many_files: `Up to ${CODE_ATTACHMENT_MAX_FILES} files per message.`,
  files_too_large: `Files can add up to ${CODE_ATTACHMENT_MAX_TOTAL_BYTES / (1024 * 1024)} MB per message.`,
};

export function codeAttachmentLimitProblem(
  files: readonly { size: number }[],
): CodeAttachmentLimitProblem | null {
  if (files.length > CODE_ATTACHMENT_MAX_FILES) {
    return 'too_many_files';
  }
  const total = files.reduce((sum, file) => sum + file.size, 0);
  return total > CODE_ATTACHMENT_MAX_TOTAL_BYTES ? 'files_too_large' : null;
}

/** The types the model sees: what OpenCode sends as media rather than dropping. */
export const MODEL_VISIBLE_ATTACHMENT_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'application/pdf',
] as const;

/** `image/PNG; charset=…` → `image/png`. */
export function normalizeAttachmentType(contentType: string): string {
  return (contentType.split(';')[0] ?? '').trim().toLowerCase();
}

export function isModelVisibleAttachmentType(contentType: string): boolean {
  return (MODEL_VISIBLE_ATTACHMENT_TYPES as readonly string[]).includes(
    normalizeAttachmentType(contentType),
  );
}

export function isImageAttachmentType(contentType: string): boolean {
  return normalizeAttachmentType(contentType).startsWith('image/');
}

/** An image the model sees, which the composer and the transcript show as a thumbnail. */
export function isPreviewedAttachment(contentType: string): boolean {
  return isImageAttachmentType(contentType) && isModelVisibleAttachmentType(contentType);
}

export const IMAGE_ATTACHMENT_ACCEPT =
  MODEL_VISIBLE_ATTACHMENT_TYPES.filter(isPreviewedAttachment).join(',');

/**
 * The images and PDFs among `files` that a session on `model` cannot read:
 * none on a model that reads them. Other files reach any model's agent as
 * sandbox paths.
 */
export function unreadableAttachments<T extends { contentType: string }>(
  model: string,
  files: readonly T[],
): T[] {
  return codeSessionModelAcceptsImages(model)
    ? []
    : files.filter((file) => isModelVisibleAttachmentType(file.contentType));
}

/** `2400000` → `2.3 MB`. */
export function formatAttachmentSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  const kilobytes = bytes / 1024;
  if (kilobytes < 1024) {
    return `${Math.round(kilobytes)} KB`;
  }
  const megabytes = kilobytes / 1024;
  return `${megabytes < 10 ? megabytes.toFixed(1) : Math.round(megabytes)} MB`;
}

/**
 * In UTF-8 bytes, which is how Linux limits a path segment (255), with room
 * for the `<n>-` the sandbox path prefixes.
 */
const MAX_ATTACHMENT_NAME_BYTES = 200;

/** `text` cut to whole characters that fit in `maxBytes` of UTF-8. */
function truncateUtf8(text: string, maxBytes: number): string {
  const encoder = new TextEncoder();
  let bytes = 0;
  let kept = '';
  for (const character of text) {
    bytes += encoder.encode(character).length;
    if (bytes > maxBytes) {
      break;
    }
    kept += character;
  }
  return kept;
}

/**
 * A file name safe to show and to use as the last segment of a sandbox path:
 * no directories or control characters, and short enough in bytes for the
 * file system. Dotfiles such as `.env.example` keep their name; the sandbox
 * path prefixes it, so it never hides the file.
 */
export function sanitizeAttachmentName(name: string): string {
  const base = name.split(/[/\\]/).at(-1) ?? '';
  const cleaned = truncateUtf8(
    // eslint-disable-next-line no-control-regex -- stripping control characters is the point
    base.replace(/[\u0000-\u001F\u007F]/g, '').trim(),
    MAX_ATTACHMENT_NAME_BYTES,
  );
  return cleaned === '' || cleaned === '.' || cleaned === '..' ? 'attachment' : cleaned;
}

/** What a client sends for each file with a prompt: the upload's ID and the file's name. */
export const codeAttachmentRefSchema = z.object({
  id: z.string().guid(),
  name: z.string().min(1).max(255),
});
export type CodeAttachmentRef = z.infer<typeof codeAttachmentRefSchema>;

export const codeAttachmentRefsSchema = z
  .array(codeAttachmentRefSchema)
  .max(CODE_ATTACHMENT_MAX_FILES)
  .refine(
    (refs) => new Set(refs.map((ref) => ref.id)).size === refs.length,
    'Each file can be attached once',
  );

/** A file saved on a turn. Everything but the name comes from the store, not the client. */
export const codeTurnAttachmentSchema = z.object({
  id: z.string().guid(),
  name: z.string(),
  contentType: z.string(),
  size: z.number().int().nonnegative(),
  /** Where the bytes are in the attachments store: `<installationId>/<repositoryId>/<id>`. */
  pathname: z.string(),
});
export type CodeTurnAttachment = z.infer<typeof codeTurnAttachmentSchema>;

/** The files saved on a turn row; a value that does not parse counts as none. */
export function turnAttachments(value: unknown): CodeTurnAttachment[] {
  const parsed = z.array(codeTurnAttachmentSchema).safeParse(value);
  return parsed.success ? parsed.data : [];
}
